import { QueryClient, useMutation, useQueryClient } from 'react-query';
import { IN_APP_NOTIFICATIONS_QUERY } from 'components/in-app-notifications/InAppNotifications.constants';
import { InAppNotification, NotificationCount } from 'types/InAppNotification';
import { openUrl } from 'utils/url';
import InAppNotificationsApi from 'app/api/inAppNotifications';
import { modalRoutes } from 'constants/routes';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';

type NotificationsInfiniteData = {
  pageParams: unknown[];
  pages: InAppNotification[][];
};

type NotificationsSnapshot = {
  notifications?: NotificationsInfiniteData;
  count?: NotificationCount;
};

const COUNT_QUERY_KEY = IN_APP_NOTIFICATIONS_QUERY.NOTICIATIONS_COUNT;
const LIST_QUERY_KEY = [IN_APP_NOTIFICATIONS_QUERY.RETRIEVE];

async function snapshotNotifications(
  queryClient: QueryClient,
): Promise<NotificationsSnapshot> {
  await Promise.all([
    queryClient.cancelQueries(LIST_QUERY_KEY),
    queryClient.cancelQueries(COUNT_QUERY_KEY),
  ]);

  return {
    notifications:
      queryClient.getQueryData<NotificationsInfiniteData>(LIST_QUERY_KEY),
    count: queryClient.getQueryData<NotificationCount>(COUNT_QUERY_KEY),
  };
}

function restoreNotifications(
  queryClient: QueryClient,
  snapshot: NotificationsSnapshot,
) {
  if (snapshot.notifications) {
    queryClient.setQueryData(LIST_QUERY_KEY, snapshot.notifications);
  }

  if (snapshot.count) {
    queryClient.setQueryData(COUNT_QUERY_KEY, snapshot.count);
  }
}

function setUnseenCount(queryClient: QueryClient, count: number) {
  queryClient.setQueryData<NotificationCount | undefined>(
    COUNT_QUERY_KEY,
    (previous) => (previous ? { count } : previous),
  );
}

function updateCachedNotifications(
  queryClient: QueryClient,
  updateNotification: (notification: InAppNotification) => InAppNotification,
) {
  queryClient.setQueryData<NotificationsInfiniteData | undefined>(
    LIST_QUERY_KEY,
    (previous) =>
      previous
        ? {
            ...previous,
            pages: previous.pages.map((page) => page.map(updateNotification)),
          }
        : previous,
  );
}

function applyReadState(queryClient: QueryClient, id: number, isRead: boolean) {
  let didChange = false;

  updateCachedNotifications(queryClient, (notification) => {
    if (notification.id !== id || notification.is_read === isRead) {
      return notification;
    }

    didChange = true;

    return { ...notification, is_read: isRead };
  });

  if (!didChange) {
    return;
  }

  queryClient.setQueryData<NotificationCount | undefined>(
    COUNT_QUERY_KEY,
    (previous) =>
      previous
        ? { count: Math.max(0, previous.count + (isRead ? -1 : 1)) }
        : previous,
  );
}

function useOptimisticNotificationsMutation<TData>(
  mutationFn: () => Promise<TData>,
  applyOptimisticUpdate: (queryClient: QueryClient) => void,
) {
  const queryClient = useQueryClient();

  return useMutation(mutationFn, {
    onMutate: async () => {
      const snapshot = await snapshotNotifications(queryClient);

      applyOptimisticUpdate(queryClient);

      return snapshot;
    },
    onError: (_error, _variables, snapshot) => {
      if (snapshot) {
        restoreNotifications(queryClient, snapshot);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(COUNT_QUERY_KEY);
    },
  });
}

export const useMarkAllNotificationsAsRead = () =>
  useOptimisticNotificationsMutation(
    InAppNotificationsApi.markAllAsRead,
    (queryClient) => {
      setUnseenCount(queryClient, 0);
      updateCachedNotifications(queryClient, (notification) =>
        notification.is_read
          ? notification
          : { ...notification, is_read: true },
      );
    },
  );

export const useToggleNotificationRead = (id: number, isRead: boolean) =>
  useOptimisticNotificationsMutation(
    () => InAppNotificationsApi.toggleRead(id, isRead),
    (queryClient) => applyReadState(queryClient, id, !isRead),
  );

export const useMarkNotificationAsRead = (id: number) =>
  useOptimisticNotificationsMutation(
    () => InAppNotificationsApi.toggleRead(id, false),
    (queryClient) => applyReadState(queryClient, id, true),
  );

export const useMarkAllNotificationsAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation(InAppNotificationsApi.markAllAsSeen, {
    onMutate: async () => {
      await queryClient.cancelQueries(COUNT_QUERY_KEY);

      const count =
        queryClient.getQueryData<NotificationCount>(COUNT_QUERY_KEY);

      setUnseenCount(queryClient, 0);

      return { count };
    },
    onError: (_error, _variables, snapshot) => {
      if (snapshot) {
        restoreNotifications(queryClient, snapshot);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(COUNT_QUERY_KEY);
    },
  });
};

export const useInAppNotificationClick = (notification: InAppNotification) => {
  const { mutate: markAsRead } = useMarkNotificationAsRead(notification.id);
  const isNewPostExperienceActive = usePostsLayoutState(
    (state) => state.isNewPostExperienceActive,
  );

  return () => {
    if (!notification.is_read) {
      markAsRead();
    }

    if (notification.link === null || !notification.link?.length) {
      return;
    }

    let link = notification.link;

    if (isNewPostExperienceActive) {
      link = link.replace(
        `#${modalRoutes.POST_DETAIL}`,
        `#${modalRoutes.POST_DETAIL_NEW}`,
      );
    }

    return openUrl(link);
  };
};
